import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { RawIntlProvider, createIntlCache, type IntlConfig } from 'react-intl';
import { flattenMessages } from './utils';
import { createAppIntl } from './createAppIntl';
import { useAuth } from '@/hooks/useAuth';
import {
  type SupportedLocale,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  setStoredLocale,
  isValidLocale,
  getInitialLocale,
  getFormattingLocale,
} from '@/utils/localeStorage';

// Importar mensajes anidados
import enMessagesNested from '../locales/en.json';
import esMessagesNested from '../locales/es.json';

interface LocaleContextType {
  locale: SupportedLocale;
  changeLocale: (newLocale: SupportedLocale) => Promise<void>;
  isChangingLocale: boolean;
  /** Full locale for formatting (e.g., 'es-US' for Spanish with US date/number format) */
  formattingLocale: string;
}

// Aplanar los mensajes para react-intl
const enMessages = flattenMessages(enMessagesNested);

// EX-03: es layers over en. `defaultLocale` only silences the missing-key
// error; without the merge the key path itself renders on screen.
const messages: Record<SupportedLocale, Record<string, string>> = {
  en: enMessages,
  es: { ...enMessages, ...flattenMessages(esMessagesNested) },
};

const intlCache = createIntlCache();

const onIntlError: IntlConfig['onError'] = (err) => {
  // Silenciar errores de mensajes faltantes en desarrollo
  if (err.code === 'MISSING_TRANSLATION') {
    // eslint-disable-next-line no-console
    console.warn(`Missing translation: ${err.message}`);
    return;
  }
  // eslint-disable-next-line no-console
  console.error(err);
};

/**
 * Determine initial locale based on priority:
 * 1. User preference from API (if authenticated)
 * 2. localStorage
 * 3. Browser language
 * 4. Default (English)
 * 
 * Note: User API preference is passed as parameter
 */
function determineInitialLocale(userLocale?: string | null): SupportedLocale {
  // Priority 1: User preference from API
  if (userLocale && isValidLocale(userLocale)) {
    return userLocale;
  }
  
  // Priority 2 & 3: localStorage then browser detection
  return getInitialLocale();
}

/**
 * Sync locale preference with backend
 * TODO: Enable when GET/PATCH /api/users/me/preferences is implemented
 * Returns true if successful, false otherwise
 */
async function syncLocaleWithBackend(_locale: SupportedLocale): Promise<boolean> {
  // Backend endpoint not yet implemented — localStorage is the source of truth
  return false;
}

export const LocaleContext = createContext<LocaleContextType>({
  locale: DEFAULT_LOCALE,
  changeLocale: async () => {},
  isChangingLocale: false,
  formattingLocale: DEFAULT_LOCALE,
});

/**
 * Hook to access locale context
 * Provides locale, changeLocale function, and loading state
 */
export const useLocale = () => useContext(LocaleContext);

interface IntlProviderProps {
  children: ReactNode;
}

export function IntlProvider({ children }: IntlProviderProps) {
  const { user, isAuthenticated } = useAuth();
  
  // TODO: When backend supports user preferences, get locale from user object
  // const userLocale = user?.preferences?.locale;
  const userLocale: string | null = null;
  
  const [locale, setLocale] = useState<SupportedLocale>(() => determineInitialLocale(userLocale));
  const [isChangingLocale, setIsChangingLocale] = useState(false);
  
  // Get formatting locale (e.g., 'es-US' for Spanish with US formatting)
  const formattingLocale = getFormattingLocale(locale);

  // 868mqhj74: numbers, percentages and money format with formattingLocale;
  // intl.locale stays the language.
  const intl = useMemo(
    () => createAppIntl(
      { locale, messages: messages[locale], defaultLocale: DEFAULT_LOCALE, onError: onIntlError },
      intlCache,
    ),
    [locale],
  );

  // Sync locale when user logs in with a saved preference
  useEffect(() => {
    if (isAuthenticated && userLocale && isValidLocale(userLocale)) {
      // User has a preference saved in their profile, use it
      setLocale(userLocale);
      // Also update localStorage to keep them in sync
      setStoredLocale(userLocale);
    }
  }, [isAuthenticated, userLocale]);

  // Sincronizar lang attribute al montar y cuando cambie el locale
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  /**
   * Change locale with optimistic update
   * Updates UI immediately, then syncs to backend if authenticated
   */
  const changeLocale = useCallback(async (newLocale: SupportedLocale): Promise<void> => {
    if (!isValidLocale(newLocale)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid locale: ${newLocale}`);
      return;
    }

    if (newLocale === locale) {
      return; // No change needed
    }

    setIsChangingLocale(true);

    try {
      // Optimistic update: change UI and localStorage immediately
      setLocale(newLocale);
      setStoredLocale(newLocale);
      
      // Update document lang attribute
      document.documentElement.lang = newLocale;

      // Sync with backend if authenticated (fire and forget, don't block UI)
      if (isAuthenticated && user) {
        syncLocaleWithBackend(newLocale).then((success) => {
          if (!success) {
            // eslint-disable-next-line no-console
            console.warn('Locale synced locally but backend sync failed');
          }
        });
      }
    } finally {
      setIsChangingLocale(false);
    }
  }, [locale, isAuthenticated, user]);

  return (
    <LocaleContext.Provider value={{ locale, changeLocale, isChangingLocale, formattingLocale }}>
      <RawIntlProvider value={intl}>
        {children}
      </RawIntlProvider>
    </LocaleContext.Provider>
  );
}

// Re-export types for convenience
export { SUPPORTED_LOCALES, type SupportedLocale };
